import { type User } from 'oidc-client-ts';
import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { RedirectUrlUtils } from 'common/utils/redirect-url-utils';
import { SpinnerPage } from '../../routes/spinner-page';
import { history } from '../../store';
import { setAccessToken } from './auth-token';
import {
  ACCOUNT_AUTH_MODULAR_SIGNUP_PATH_RE,
  OIDC_CALLBACK_PATH,
  OIDC_REGISTER_PATH,
  SILENT_CALLBACK_PATH,
} from './constants';
import {
  extraParamsFromRegisterUrlSearch,
  signinRegistrationRedirect,
} from './registration-signin';
import { userManager } from './user-manager';

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);


const SESSION_STORAGE_RETURN_PATH_KEY = 'oidc_return_path';
const APP_LOGOUT_URL = `${RedirectUrlUtils.getAppRedirectUrl()}/oauth/logout`;

function getCurrentAppPath(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function storeReturnPath(returnPath: string): void {
  sessionStorage.setItem(SESSION_STORAGE_RETURN_PATH_KEY, returnPath);
}

function navigateToReturnPath(returnPath: string): void {
  const nextUrl = new URL(returnPath, window.location.origin);

  if (nextUrl.origin === window.location.origin) {
    history.replace(`${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    return;
  }

  window.location.assign(nextUrl.href);
}

function startSigninRedirect(returnPath: string = getCurrentAppPath()): Promise<void> {
  storeReturnPath(returnPath);
  return userManager.signinRedirect();
}

async function handleSilentCallback(setIsLoading: (isLoading: boolean) => void): Promise<void> {
  await userManager.signinSilentCallback();
  setIsLoading(false);
}

async function handleOidcCallback(setUser: (user: User) => void): Promise<void> {
  const callbackUser = await userManager.signinRedirectCallback();
  setUser(callbackUser);
  setAccessToken(callbackUser.access_token);
  const returnPath = sessionStorage.getItem(SESSION_STORAGE_RETURN_PATH_KEY) ?? '/';
  sessionStorage.removeItem(SESSION_STORAGE_RETURN_PATH_KEY);
  navigateToReturnPath(returnPath);
}

function getFullPlanId(planId: string | undefined): string {
  return planId ? `skema-modular-${planId}` : '';
}

async function handleAccountAuthModularSignup(pathname: string): Promise<void> {
  const modularSignupMatch = pathname.match(ACCOUNT_AUTH_MODULAR_SIGNUP_PATH_RE);
  const returnUrl = new URL(OIDC_REGISTER_PATH, window.location.origin);
  returnUrl.searchParams.set('plan_proposal', modularSignupMatch?.[1] ?? '');
  returnUrl.searchParams.set('plan_id', getFullPlanId(modularSignupMatch?.[2]));
  await userManager.removeUser();
  window.location.assign(
    `${APP_LOGOUT_URL}?returnUrl=${encodeURIComponent(returnUrl.toString())}`,
  );
}

async function handleOidcRegister(): Promise<void> {
  storeReturnPath(RedirectUrlUtils.getAppRedirectUrl());
  const extraParams = extraParamsFromRegisterUrlSearch(window.location.search);
  await signinRegistrationRedirect(userManager, extraParams);
}

async function handleDefault(setUser: (user: User) => void): Promise<void> {
  const existingUser = await userManager.getUser();
  if (existingUser && !existingUser.expired) {
    setUser(existingUser);
    setAccessToken(existingUser.access_token);
  } else {
    userManager.signinSilent().catch(() => {
      void startSigninRedirect();
    });
  }
}

export const OidcAuthProvider = ({ children }: React.PropsWithChildren<{}>): JSX.Element => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const handleUserLoaded = (loadedUser: User): void => {
      setUser(loadedUser);
      setAccessToken(loadedUser.access_token);
    };

    const handleUserUnloaded = (): void => {
      setUser(null);
      setAccessToken(null);
    };

    const handleAccessTokenExpired = (): void => {
      userManager.signinSilent().catch(() => {
        void startSigninRedirect();
      });
    };

    const handleUserSignedOut = (): void => {
      window.location.assign(APP_LOGOUT_URL);
    };

    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserUnloaded(handleUserUnloaded);
    userManager.events.addAccessTokenExpired(handleAccessTokenExpired);
    userManager.events.addUserSignedOut(handleUserSignedOut);

    const initAuth = async (): Promise<void> => {
      try {
        const pathname = window.location.pathname;
        switch (true) {
          case pathname.includes(SILENT_CALLBACK_PATH):
            return await handleSilentCallback(setIsLoading);
          case pathname.includes(OIDC_CALLBACK_PATH):
            return await handleOidcCallback(setUser);
          case ACCOUNT_AUTH_MODULAR_SIGNUP_PATH_RE.test(pathname):
            return await handleAccountAuthModularSignup(pathname);
          case pathname === OIDC_REGISTER_PATH:
            return await handleOidcRegister();
          default:
            return await handleDefault(setUser);
        }
      } catch (error) {
        console.error('[OidcAuth] initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      userManager.events.removeAccessTokenExpired(handleAccessTokenExpired);
      userManager.events.removeUserSignedOut(handleUserSignedOut);
    };
  }, []);

  const contextValue: AuthContextValue = {
    user,
    isLoading,
    signIn: () => startSigninRedirect(),
    signOut: async () => {
      await userManager.removeUser();
      window.location.assign(APP_LOGOUT_URL);
    },
  };

  if (isLoading || !user) {
    return <SpinnerPage />;
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useOidcAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useOidcAuth must be used within OidcAuthProvider');
  }
  return context;
};
